<template>
  <div>
    <!-- 遮罩层 -->
    <transition name="fade">
      <div 
        v-if="visible && mask" 
        class="custom-drawer-mask"
        @click="handleMaskClick"
      ></div>
    </transition>
    
    <!-- 抽屉内容 -->
    <transition :name="transitionName">
      <div 
        v-show="visible" 
        class="custom-drawer"
        :class="`custom-drawer-${placement}`"
        :style="drawerStyle"
      >
        <!-- 抽屉标题 -->
        <div v-if="title || $slots.title" class="drawer-header">
          <slot name="title">
          </slot>
        </div>
        
        <!-- 抽屉内容区域 -->
        <div class="drawer-body" :style="bodyStyle">
          <slot></slot>
        </div>
        
        <!-- 抽屉底部 -->
        <div v-if="$slots.footer" class="drawer-footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'CustomDrawer',
  model: {
    prop: 'visible',
    event: 'update:visible'
  },
  props: {
    // 是否显示抽屉
    visible: {
      type: Boolean,
      default: false
    },
    // 抽屉标题
    title: {
      type: String,
      default: ''
    },
    // 抽屉宽度
    width: {
      type: [String, Number],
      default: 450
    },
    // 抽屉方向：right, left, top, bottom
    placement: {
      type: String,
      default: 'right',
      validator: value => ['right', 'left', 'top', 'bottom'].includes(value)
    },
    // 是否显示遮罩
    mask: {
      type: Boolean,
      default: true
    },
    // 点击遮罩是否关闭抽屉
    maskClosable: {
      type: Boolean,
      default: true
    },
    // 是否显示关闭按钮
    closable: {
      type: Boolean,
      default: true
    },
    // 抽屉内容区域样式
    bodyStyle: {
      type: Object,
      default: () => ({})
    },
    // 是否锁定背景滚动
    lockScroll: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    // 根据位置计算过渡动画名称
    transitionName() {
      return `slide-${this.placement}`
    },
    // 根据位置和宽度计算抽屉样式
    drawerStyle() {
      const style = {}
      
      // 根据位置设置宽度或高度
      if (this.placement === 'right' || this.placement === 'left') {
        style.width = typeof this.width === 'number' ? `${this.width}px` : this.width
      } else {
        style.height = typeof this.width === 'number' ? `${this.width}px` : this.width
      }
      
      return style
    },
    // 计算是否显示关闭按钮
    showClose() {
      return this.closable
    }
  },
  watch: {
    visible(val) {
      if (val && this.lockScroll) {
        this.lockBodyScroll()
      } else {
        this.unlockBodyScroll()
      }
    }
  },
  methods: {
    // 处理遮罩点击
    handleMaskClick() {
      if (this.maskClosable) {
        this.handleClose()
      }
    },
    // 处理关闭
    handleClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    // 锁定背景滚动
    lockBodyScroll() {
      const body = document.body
      body.style.overflow = 'hidden'
      body.style.position = 'fixed'
      body.style.width = '100%'
    },
    // 解锁背景滚动
    unlockBodyScroll() {
      const body = document.body
      body.style.overflow = ''
      body.style.position = ''
      body.style.width = ''
    }
  },
  beforeDestroy() {
    this.unlockBodyScroll()
  }
}
</script>

<style scoped>
/* 遮罩层样式 */
.custom-drawer-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 999;
}

/* 抽屉基础样式 */
.custom-drawer {
  position: absolute;
  z-index: 1000;
  background-color: #fff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  border-radius: 6px;
}

/* 不同位置的抽屉样式 */
.custom-drawer-right {
  top: 0;
  right: 0;
  height: 100%;
}

.custom-drawer-left {
  top: 0;
  left: 0;
  height: 100%;
}

.custom-drawer-top {
  top: 0;
  left: 0;
  width: 100%;
}

.custom-drawer-bottom {
  bottom: 0;
  left: 0;
  width: 100%;
}

/* 抽屉内容区域 */
.drawer-header {
  padding: 10px;
  border-bottom: 1px solid #e8e8e8;
  background-color: #F8F8F8;
  border-radius: 6px;
}

.drawer-body {
  overflow: auto;
}

.drawer-footer {
  padding: 10px 16px;
  border-top: 1px solid #e8e8e8;
  text-align: right;
  background: #fff;
}

/* 遮罩层过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* 右侧抽屉动画 */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}

.slide-right-enter,
.slide-right-leave-to {
  transform: translateX(100%);
}

/* 左侧抽屉动画 */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.3s ease;
}

.slide-left-enter,
.slide-left-leave-to {
  transform: translateX(-100%);
}

/* 顶部抽屉动画 */
.slide-top-enter-active,
.slide-top-leave-active {
  transition: transform 0.3s ease;
}

.slide-top-enter,
.slide-top-leave-to {
  transform: translateY(-100%);
}

/* 底部抽屉动画 */
.slide-bottom-enter-active,
.slide-bottom-leave-active {
  transition: transform 0.3s ease;
}

.slide-bottom-enter,
.slide-bottom-leave-to {
  transform: translateY(100%);
}
</style>